import { useEffect, useRef, useState } from "react";
import {
  deleteProject,
  listProjects,
  purgeEphemeralProjects,
  type ProjectSummary,
} from "../model/storage.ts";

/**
 * The saved-projects list (Landing.tsx's own list) plus every IndexedDB
 * failure surface. Every IndexedDB call used to be fire-and-forget with no
 * `.catch()` — a quota-exceeded error, a blocked upgrade, or private-
 * browsing restrictions silently stopped the debounced autosave, and the
 * user lost work on reload without knowing. Every `saveProject`/
 * `loadProject`/`deleteProject` call site across App.tsx reports through
 * `reportStorageError` instead.
 */
export function useProjectStorage() {
  const [savedProjects, setSavedProjects] = useState<ProjectSummary[]>([]);
  const [storageError, setStorageError] = useState<string | null>(null);
  // Monotonic request id (same pattern as ProjectionPicker.tsx's own
  // debounced search, and useProjectLifecycle.ts's own openSavedProject) —
  // `refreshSavedProjects` is called from 7+ independent sites (every
  // save/delete/duplicate/rename across the app), and its own `listProjects`
  // is an async IndexedDB read with no ordering guarantee: an OLDER call's
  // promise could resolve AFTER a NEWER one's, transiently showing a stale
  // (e.g. just-deleted) project until the next refresh self-corrects it.
  const refreshRequestIdRef = useRef(0);

  function reportStorageError(err: unknown) {
    console.error("Storage operation failed:", err);
    setStorageError(err instanceof Error ? err.message : String(err));
  }

  // Handles the error here (rather than re-throwing) so every
  // `.then(refreshSavedProjects)` caller's own promise chain still resolves
  // normally, with the failure already reported by the time it does.
  const refreshSavedProjects = () => {
    const requestId = ++refreshRequestIdRef.current;
    return listProjects()
      .then((projects) => {
        if (requestId !== refreshRequestIdRef.current) return; // superseded by a newer refresh
        setSavedProjects(projects);
      })
      .catch((err: unknown) => {
        if (requestId !== refreshRequestIdRef.current) return; // superseded by a newer refresh
        reportStorageError(err);
      });
  };

  // Drop any leftover ephemeral sample before the very first list read, so
  // a stale one never flashes in "Your projects" for a frame.
  useEffect(() => {
    purgeEphemeralProjects().then(refreshSavedProjects).catch(reportStorageError);
    // Runs once, on mount, by design — refreshSavedProjects/reportStorageError
    // are recreated every render, so listing them would refire this on
    // every render instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function deleteSavedProject(id: string) {
    try {
      await deleteProject(id);
    } catch (err) {
      reportStorageError(err);
      return;
    }
    refreshSavedProjects();
  }

  return {
    savedProjects,
    storageError,
    setStorageError,
    reportStorageError,
    refreshSavedProjects,
    deleteSavedProject,
  };
}
